import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { FallaufgabeLernbegleiter, TeilaufgabenSolution } from '@reflact/ipkv-types';
import { ConfirmationService } from 'primeng/api';
import { Button } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { Dialog } from "primeng/dialog";
import { Api } from '../api';
import { OutroService } from '../outro/outro.service';
import { ChatComponent } from "./chat/chat.component";
import { ChatService } from './chat/chat.service';
import { ClockService } from './clock-service';
import { DisconnectionService } from './disconnection.service';
import { SolutionsComponent } from "./solutions/solutions.component";
import { SubmitSolutionService } from './submit-solution.service';

@Component({
  standalone: true,
  imports: [Button, Dialog, CommonModule, CheckboxModule, ChatComponent, FormsModule, SolutionsComponent],
  templateUrl: './lernbegleiter.component.html',
  styleUrl: './lernbegleiter.component.scss'
})
export class LernbegleiterComponent implements OnInit {
  private disclaimerStorageKey = 'lernbegleiter.disclaimer.hidden.v1';

  public api = inject(Api);
  public router = inject(Router);
  public confirmationService = inject(ConfirmationService);
  public clockService = inject(ClockService);
  public outroService = inject(OutroService);
  public disconnectionService = inject(DisconnectionService);

  public chatService = inject(ChatService);
  public submitService = inject(SubmitSolutionService);
  public showHints = false;
  public progressUrl = "https://eli.reflact.info/ipkv/progress/progress1.png";
  public showDisclaimerModal = false;
  public dontShowDisclaimerAgain = false;
  public chatUnlocked = false;

  ngOnInit(): void {
    this.initDisclaimerState();

    this.clockService.timeIsOver.subscribe((val) => {
      if (val) {
        this.outroService.navigateToOutro(this.outroService.OUTRO_REASON_TIMEISUP);
      }
    });
    this.api.solutions.subscribe(s => {
      this.setProgressUrl(s);
    });
  }

  private initDisclaimerState() {
    const hideDisclaimer = this.getHideDisclaimerFlag();
    this.showDisclaimerModal = !hideDisclaimer;
    this.chatUnlocked = hideDisclaimer;
  }

  private getHideDisclaimerFlag(): boolean {
    try {
      return localStorage.getItem(this.disclaimerStorageKey) === '1';
    } catch {
      return false;
    }
  }

  private setHideDisclaimerFlag(value: boolean) {
    try {
      if (value) {
        localStorage.setItem(this.disclaimerStorageKey, '1');
      } else {
        localStorage.removeItem(this.disclaimerStorageKey);
      }
    } catch {
      // localStorage can fail in restricted browser contexts; ignore and continue.
    }
  }

  public confirmDisclaimer() {
    this.setHideDisclaimerFlag(this.dontShowDisclaimerAgain);
    this.chatUnlocked = true;
    this.showDisclaimerModal = false;
  }

  public async showSolutionsDialog(includeSubmit: boolean) {
    // Toggle: Wenn bereits offen mit gleicher Einstellung, schließen
    if (this.submitService.solutionsVisible && this.submitService.includeSubmit === includeSubmit) {
      this.submitService.solutionsVisible = false;
    } else {
      this.submitService.solutionsVisible = true;
      this.submitService.includeSubmit = includeSubmit;
    }
  }

  public setProgressUrl(solutions: TeilaufgabenSolution[]) {
    const teilaufgabenAnzahl = this.api.fallaufgabe?.aufgaben.map(a => a.teilaufgaben.length).reduce((partialSum, a) => partialSum + a, 0);
    if (!teilaufgabenAnzahl) return;
    const antwortenAnzahl = solutions.length;
    const percentage = Math.round((antwortenAnzahl / teilaufgabenAnzahl) * 100);
    this.progressUrl = "https://eli.reflact.info/ipkv/progress/progress" + (percentage + 1) + ".png"
  }

  public aufgabeInProgress(aufgabe: FallaufgabeLernbegleiter["aufgaben"][number]): boolean {
    if (!this.api.fallaufgabe) return false;
    return aufgabe.teilaufgaben.some(a => this.api.solutions.value.some(ta => a._id == ta.teilaufgabenId));
  }

  public aufgabeCompleted(aufgabe: FallaufgabeLernbegleiter["aufgaben"][number]): boolean {
    if (!this.api.fallaufgabe) return false;
    return aufgabe.teilaufgaben.every(ta => {
      return this.api.solutions.value.some(s => ta._id == s.teilaufgabenId);
    });
  }
}